// Lib
import { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { AnyObject, ObjectSchema } from "yup";
import { Spin } from "antd";
// Api
import {
  useGetLocationQuery,
  useUpdateLocationMutation,
} from "rtkQuery/query/locationsAPI";
// Hooks
import { useNotification, useViewport } from "hooks";
// Types
import { GeometryData } from "types/locations";
// Theme
// Constants
import { ADMIN_ROUTES, NOTIFICATIONS, rtkQueryParams } from "consts";
// Helpers
import {
  formatKitchenCoordinates,
  getValidation,
  parseKitchenCoordinates,
} from "./helpers";
// Utils
import { errorHandler } from "utils/errorHandler";
// Icons
import { LoaderIcon } from "icons";
// Components
import { ArrowBackLink } from "components";
import { Input, InputNumber, Switch } from "components/Form";
// Styled
import {
  ContentBox,
  FlexContainer,
  InputsGridContainer,
  PageHeadingContainer,
  PageTitleContainer,
} from "styled/Box";
import { Typography } from "styled/Typography";
import { Button } from "styled/Buttons";
import { InputsGrid } from "./styled";

type ValidationType = ObjectSchema<
  {
    name: string;
    zones: object;
    pickupEnabled?: boolean;
    deliveryEnabled?: boolean;
    pickupHoursFrom?: string;
    pickupHoursTo?: string;
    pickupAddressCity?: string;
    pickupAddressArea?: string;
    pickupAddressBlock?: string;
    pickupAddressStreet?: string;
    pickupAddressBuilding?: string;
    kitchenCoordinates?: string;
    notificationWhatsappNumber?: string;
  },
  AnyObject,
  {
    name: undefined;
    zones: object;
    pickupEnabled: undefined;
    deliveryEnabled: undefined;
    pickupHoursFrom: undefined;
    pickupHoursTo: undefined;
    pickupAddressCity: undefined;
    pickupAddressArea: undefined;
    pickupAddressBlock: undefined;
    pickupAddressStreet: undefined;
    pickupAddressBuilding: undefined;
    kitchenCoordinates: undefined;
    notificationWhatsappNumber: undefined;
  },
  ""
>;

type AreaForm = {
  name: string;
  zones: { [x: string]: { name: string; priority: number } };
  pickupEnabled: boolean;
  deliveryEnabled: boolean;
  pickupHoursFrom: string;
  pickupHoursTo: string;
  pickupAddressCity: string;
  pickupAddressArea: string;
  pickupAddressBlock: string;
  pickupAddressStreet: string;
  pickupAddressBuilding: string;
  kitchenCoordinates: string;
  notificationWhatsappNumber: string;
};

export const EditArea: FC = () => {
  const { id } = useParams();

  const { openNotification } = useNotification();

  const { isMobile } = useViewport();

  const [geometry, setGeometry] = useState<
    Record<string, { geometry: GeometryData }>
  >({});

  const [validation, setValidation] = useState<ValidationType>();

  const [update, { isLoading: isUpdateLoading }] = useUpdateLocationMutation();
  const { data: locationData, isFetching: isDataLoading } = useGetLocationQuery(
    { id },
    rtkQueryParams,
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<AreaForm>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    resolver: validation ? yupResolver(validation) : null,
  });

  const { operatingZones } = locationData || {};

  const pickupEnabled = watch("pickupEnabled");
  const kitchenCoordinates = parseKitchenCoordinates(
    watch("kitchenCoordinates"),
  );

  useEffect(() => {
    if (!locationData) {
      return;
    }

    const {
      name,
      operatingZones,
      pickupEnabled,
      deliveryEnabled,
      pickupHours,
      pickupAddress,
      kitchenAddress,
      notificationWhatsappNumber,
    } = locationData || {};

    const fields = operatingZones.reduce(
      (acc, el, i) => {
        acc.zones[`zone${i}`] = { name: el.name, priority: el.priority };
        acc.geometry[`zone${i}`] = { geometry: el.geometry };
        return acc;
      },

      { zones: {}, geometry: {} },
    );

    reset({
      name,
      zones: fields.zones,
      pickupEnabled: pickupEnabled ?? false,
      deliveryEnabled: deliveryEnabled ?? true,
      pickupHoursFrom: pickupHours?.from ?? "",
      pickupHoursTo: pickupHours?.to ?? "",
      pickupAddressCity: pickupAddress?.city ?? "",
      pickupAddressArea: pickupAddress?.area ?? "",
      pickupAddressBlock: pickupAddress?.block ?? "",
      pickupAddressStreet: pickupAddress?.street ?? "",
      pickupAddressBuilding: pickupAddress?.building ?? "",
      kitchenCoordinates: formatKitchenCoordinates(kitchenAddress),
      notificationWhatsappNumber: notificationWhatsappNumber ?? "",
    });
    setGeometry(fields.geometry);

    setValidation(getValidation(locationData));
    // const validation = getValidation(data);
  }, [locationData]);

  const onSubmit: SubmitHandler<AreaForm> = async data => {
    const {
      name,
      zones,
      pickupEnabled,
      deliveryEnabled,
      pickupHoursFrom,
      pickupHoursTo,
      pickupAddressCity,
      pickupAddressArea,
      pickupAddressBlock,
      pickupAddressStreet,
      pickupAddressBuilding,
      kitchenCoordinates,
      notificationWhatsappNumber,
    } = data;

    const operatingZones = Object.entries(zones).map(el => ({
      name: el[1].name,
      priority: el[1].priority,
      geometry: geometry[el[0]].geometry,
    }));

    const pickupHours =
      pickupHoursFrom && pickupHoursTo
        ? { from: pickupHoursFrom, to: pickupHoursTo }
        : null;

    const pickupAddress =
      pickupAddressCity &&
      pickupAddressArea &&
      pickupAddressBlock &&
      pickupAddressStreet &&
      pickupAddressBuilding
        ? {
            city: pickupAddressCity,
            area: pickupAddressArea,
            block: pickupAddressBlock,
            street: pickupAddressStreet,
            building: pickupAddressBuilding,
          }
        : null;

    try {
      await update({
        ...locationData,
        name,
        operatingZones,
        pickupEnabled,
        deliveryEnabled,
        pickupHours,
        pickupAddress,
        kitchenAddress:
          parseKitchenCoordinates(kitchenCoordinates) ??
          locationData?.kitchenAddress,
        notificationWhatsappNumber: notificationWhatsappNumber?.trim() || null,
      }).unwrap();

      openNotification({ message: NOTIFICATIONS.LOCATION_UPDATE });

      // Stay on the page: saved values become the new baseline, so Save is
      // disabled again until the next change.
      reset({
        ...data,
        kitchenCoordinates: formatKitchenCoordinates(
          parseKitchenCoordinates(kitchenCoordinates) ??
            locationData?.kitchenAddress,
        ),
      });
    } catch (error) {
      errorHandler({ error, openNotification });
    }
  };

  return (
    <FlexContainer
      $fullwidth
      $column
      $gap={24}
      as="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <PageHeadingContainer>
        <PageTitleContainer $column $gap={16}>
          <ArrowBackLink
            title="Back to Delivery Areas"
            path={ADMIN_ROUTES.DELIVERY_AREAS.path}
          />

          <Typography.H1>Delivery Area</Typography.H1>
        </PageTitleContainer>

        <Button.Heading
          type="primary"
          htmlType="submit"
          loading={isUpdateLoading}
          disabled={!isDirty}
        >
          Save
        </Button.Heading>
      </PageHeadingContainer>

      <FlexContainer $fullwidth $column $gap={16}>
        <ContentBox>
          <InputsGridContainer>
            <Input label="ID" value={id} isDisabled />

            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  label="Name"
                  required
                  isDisabled={isUpdateLoading || isDataLoading}
                  {...field}
                  fieldState={fieldState}
                />
              )}
            />
          </InputsGridContainer>
        </ContentBox>

        <ContentBox $column>
          <Typography.H2>Pickup &amp; Delivery</Typography.H2>

          <FlexContainer $fullwidth $gap={32}>
            <Controller
              name="deliveryEnabled"
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <Switch
                  label="Delivery enabled"
                  formLabel
                  labelPosition="left"
                  checked={value}
                  onChange={onChange}
                  disabled={isUpdateLoading}
                  {...field}
                />
              )}
            />

            <Controller
              name="pickupEnabled"
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <Switch
                  label="Pickup enabled"
                  formLabel
                  labelPosition="left"
                  checked={value}
                  onChange={onChange}
                  disabled={isUpdateLoading}
                  {...field}
                />
              )}
            />
          </FlexContainer>

          {!!pickupEnabled && (
            <FlexContainer
              $fullwidth
              $column={isMobile}
              $width={isMobile ? "100%" : "272px"}
              $gap={16}
            >
              <Controller
                name="pickupHoursFrom"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    type="time"
                    label="Pickup from"
                    required
                    disabled={isUpdateLoading}
                    {...field}
                    fieldState={fieldState}
                  />
                )}
              />

              <Controller
                name="pickupHoursTo"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    type="time"
                    label="Pickup to"
                    required
                    disabled={isUpdateLoading}
                    {...field}
                    fieldState={fieldState}
                  />
                )}
              />
            </FlexContainer>
          )}

          {!!pickupEnabled && (
            <FlexContainer $fullwidth $column $gap={16}>
              <Typography.Title>Pickup address</Typography.Title>

              <InputsGridContainer>
                <Controller
                  name="pickupAddressCity"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      label="City"
                      required
                      disabled={isUpdateLoading}
                      {...field}
                      fieldState={fieldState}
                    />
                  )}
                />

                <Controller
                  name="pickupAddressArea"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      label="Area"
                      required
                      disabled={isUpdateLoading}
                      {...field}
                      fieldState={fieldState}
                    />
                  )}
                />

                <Controller
                  name="pickupAddressBlock"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      label="Block no."
                      required
                      disabled={isUpdateLoading}
                      {...field}
                      fieldState={fieldState}
                    />
                  )}
                />

                <Controller
                  name="pickupAddressStreet"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      label="Street"
                      required
                      disabled={isUpdateLoading}
                      {...field}
                      fieldState={fieldState}
                    />
                  )}
                />

                <Controller
                  name="pickupAddressBuilding"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      label="Building/Apartment no."
                      required
                      disabled={isUpdateLoading}
                      {...field}
                      fieldState={fieldState}
                    />
                  )}
                />
              </InputsGridContainer>
            </FlexContainer>
          )}
        </ContentBox>

        <ContentBox $column>
          <Typography.H2>Kitchen location</Typography.H2>

          <InputsGridContainer>
            <Controller
              name="kitchenCoordinates"
              control={control}
              render={({ field: { onBlur, ...field }, fieldState }) => (
                <Input
                  label="Kitchen coordinates"
                  placeholder="29.378586, 47.990341"
                  disabled={isUpdateLoading || isDataLoading}
                  allowClear
                  {...field}
                  onBlur={() => {
                    const parsed = parseKitchenCoordinates(field.value);
                    // A pasted Google Maps link is turned into "lat, lng"
                    // so the admin sees exactly what will be saved.
                    parsed && field.onChange(formatKitchenCoordinates(parsed));
                    onBlur();
                  }}
                  fieldState={fieldState}
                />
              )}
            />
          </InputsGridContainer>

          <Typography.Title>
            In Google Maps, right-click the kitchen entrance and click the
            coordinates to copy them, then paste here. A Google Maps link also
            works. Used as the courier pickup point and to pick the closest
            kitchen.{" "}
            {!!kitchenCoordinates && (
              <a
                href={`https://www.google.com/maps?q=${kitchenCoordinates.lat},${kitchenCoordinates.lng}`}
                target="_blank"
                rel="noreferrer"
              >
                Check on Google Maps
              </a>
            )}
          </Typography.Title>
        </ContentBox>

        <ContentBox $column>
          <Typography.H2>Notifications</Typography.H2>

          <InputsGridContainer>
            <Controller
              name="notificationWhatsappNumber"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  label="WhatsApp number"
                  placeholder="+96550123456"
                  disabled={isUpdateLoading || isDataLoading}
                  {...field}
                  fieldState={fieldState}
                />
              )}
            />
          </InputsGridContainer>

          <Typography.Title>
            WhatsApp number that receives a message when a customer arrives to
            pick up their order. Leave empty to disable this notification for
            the location.
          </Typography.Title>
        </ContentBox>

        <ContentBox $column>
          <Typography.H2>Operating zones</Typography.H2>

          <Spin indicator={<LoaderIcon />} spinning={isDataLoading}>
            <InputsGrid $gridRows={Math.ceil(operatingZones?.length / 2 || 0)}>
              {!!operatingZones?.length &&
                operatingZones.map((zone, i) => (
                  <FlexContainer
                    key={zone.name + i}
                    $fullwidth
                    $column={isMobile}
                    $align="center"
                    $justify="space-between"
                    $gap={8}
                  >
                    <FlexContainer>
                      <Typography.Title>{`Zone ${i}`}</Typography.Title>
                    </FlexContainer>

                    <FlexContainer
                      $column={isMobile}
                      $width={isMobile ? "100%" : "272px"}
                      $gap={16}
                    >
                      <Controller
                        name={`zones.zone${i}.name`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <Input
                            label="Name"
                            required
                            disabled={isUpdateLoading}
                            {...field}
                            fieldState={fieldState}
                          />
                        )}
                      />

                      <Controller
                        name={`zones.zone${i}.priority`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <InputNumber
                            label="Priority"
                            required
                            min={0}
                            precision={0}
                            {...field}
                            disabled={isUpdateLoading}
                            fieldState={fieldState}
                          />
                        )}
                      />
                    </FlexContainer>
                  </FlexContainer>
                ))}
            </InputsGrid>
          </Spin>
        </ContentBox>
      </FlexContainer>
    </FlexContainer>
  );
};
